import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { useShellAppContext } from '../features/shell/context/useShellAppContext';
import { pluginAppInIframe } from '../util/appUtils';
import { ROUTES } from '../app/routes/routeMap';

/**
 * ShellWizardGate — synchronous wizard routing gate rendered inside AppLayout.
 *
 * Reads wizard completion state from ShellAppContext (the already-loaded
 * session snapshot) and redirects first-install iframe users to /setup when
 * the wizard is explicitly incomplete.
 *
 * Gate conditions (ALL must be true to redirect):
 *   - running inside a parent iframe (embedded shell, not standalone)
 *   - onboardingWizardCompleted is strictly false (not null — fail-open)
 *   - wizardBypass is NOT set (user has not explicitly chosen to skip)
 *   - routeShopId is known (session is valid and contains a shop identity)
 *
 * Fail-open paths:
 *   - session absent / routeShopId null → no redirect (degraded UI path)
 *   - onboardingWizardCompleted null → no redirect (unknown, treat as done)
 *   - not in iframe → no redirect (standalone / development mode)
 *
 * Placement: first child of ShellAppContextProvider inside AppLayout so context
 * is available and the redirect fires before any layout or page content renders.
 */
export function ShellWizardGate({ children }) {
  const { onboardingWizardCompleted, wizardBypass, routeShopId } = useShellAppContext();

  if (
    pluginAppInIframe()
    && onboardingWizardCompleted === false
    && !wizardBypass
    && routeShopId
  ) {
    return <Redirect to={ROUTES.setup(routeShopId)} />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

ShellWizardGate.propTypes = {
  children: PropTypes.node.isRequired,
};
